import { FC, useEffect, useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import useFetchTransactionsHistory from '@/hooks/useApi/useFetchTransactionsHistory';
import EftFundingTransactionContainer from './EftFundingTransactionContainer';
import EftWithdrawTransactionContainer from './EftWithdrawTransactionContainer';
import AchFundingTransactionContainer from './ACHFundingTransactionContainer';
import AchWithdrawalTransactionContainer from './ACHWithdrawalTransactionContainer';
import InteracMoneyRequestTransactionContainer from './InteracMoneyRequestTransactionContainer';
import InteracBulkPayoutTransactionContainer from './InteracBulkPayoutTransactionContainer';
import VopayInstantWithdrawalContainer from './VopayInstantWithdrawalContainer';
import RefundTransactionContainer from './RefundTransactionContainer';
import CurrencyConversionTransactionContainer from './CurrencyConversionTransactionContainer';
import InboundETransferContainer from './InboundETransferContainer';
import VisaDirectPushTransactionContainer from './VisaDirectPushTransactionContainer';
import CreditCardTransactionContainer from './CreditCardTransactionContainer';
import OtherTransactionContainer from './OtherTransactionContainer';
import { Box, TransactionType, TransactionsLists, withStyles, Card, formattedCurrency } from 'vopay';
import { TransactionCard, Title, DetailsRows } from 'UI';
import styles from '../Transactions.module.scss';

const cn = withStyles(styles);

type TransactionInfoProps = {
  TransactionId: string;
};

const ReversalTransactionInfo: FC<TransactionInfoProps> = ({ TransactionId }) => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.ReversalTransactionInfo',
  });
  const currentDate = moment().utc().format('YYYY-MM-DD');
  const [isResponseParent, setIsResponseParent] = useState(false);
  const [transactionValues, setTransactionValues] = useState<TransactionsLists>();
  const [transactionValuesParent, setTransactionValuesParent] = useState<TransactionsLists>();
  const { fetchTransactionsHistory } = useFetchTransactionsHistory();

  useEffect(() => {
    fetchTransactionsHistory({
      StartDateTime: moment().subtract(5, 'years').format('YYYY-MM-DD'),
      EndDateTime: currentDate,
      TransactionID: TransactionId,
    }).then((res) => {
      if (!res.Success) return;

      const transactionHistory = Object.values(res.Transactions);

      if (isResponseParent) {
        const transactionValuesNew = transactionHistory.find(
          (x) => x.TransactionID === transactionValues?.ParentTransactionID,
        );
        setIsResponseParent(false);
        setTransactionValuesParent(transactionValuesNew);
      } else {
        const transactionValuesNew = transactionHistory.find((x) => x.TransactionType === 'Reversal');
        setTransactionValues(transactionValuesNew);
        setIsResponseParent(true);

        if (transactionValuesNew?.ParentTransactionID) {
          fetchTransactionsHistory({
            StartDateTime: moment().subtract(5, 'years').format('YYYY-MM-DD'),
            EndDateTime: currentDate,
            TransactionID: transactionValuesNew?.ParentTransactionID,
          }).then((res) => {
            if (!res.Success) return;
            const parentTransactionHistory = Object.values(res.Transactions);
            const parentTransactionValuesNew = parentTransactionHistory.find(
              (x) => x.TransactionID === transactionValuesNew?.ParentTransactionID,
            );
            setTransactionValuesParent(parentTransactionValuesNew);
          });
        }
      }
    });
  }, [TransactionId, currentDate]);

  if (!transactionValues) return null;

  const transactionDetails = [
    {
      label: t('TransactionDetails.transactionDate', { defaultValue: 'Transaction Date' }),
      value: transactionValues.TransactionDateTime,
    },
    {
      label: t('TransactionDetails.lastModified', { defaultValue: 'Last Modified' }),
      value: transactionValues.LastModified,
    },
    {
      label: t('TransactionDetails.type', { defaultValue: 'Transaction Type' }),
      value: t('reversal'),
    },
    {
      label: t('TransactionDetails.glCode', { defaultValue: 'GL Code' }),
      value: transactionValues.GLCode === '' ? '-' : transactionValues.GLCode,
    },
    {
      label: t('TransactionDetails.paymentRailID', { defaultValue: 'Payment Rail ID' }),
      value: transactionValues.PaymentRailID === '' ? '-' : transactionValues.PaymentRailID,
    },
    {
      label: t('TransactionDetails.debitAmount', { defaultValue: 'Debit Amount' }),
      value: transactionValues.DebitAmount
        ? `${transactionValues.Currency} ${formattedCurrency(transactionValues.DebitAmount)}`
        : '-',
    },
    {
      label: t('TransactionDetails.creditAmount', { defaultValue: 'Credit Amount' }),
      value: transactionValues.CreditAmount
        ? `${transactionValues.Currency} ${formattedCurrency(transactionValues.CreditAmount)}`
        : '-',
    },
    {
      label: t('TransactionDetails.holdAmount', { defaultValue: 'Hold Amount' }),
      value: transactionValues.HoldAmount
        ? `${transactionValues.Currency} ${formattedCurrency(transactionValues.HoldAmount)}`
        : '-',
    },
    {
      label: t('TransactionDetails.notes', { defaultValue: 'Notes' }),
      value: transactionValues.Notes || '-',
    },
  ];

  return (
    <>
      <Box flx="col" gapSize="24px">
        <TransactionCard
          receiverName={transactionValues.FullName}
          amount={transactionValues.DebitAmount}
          currency={transactionValues.Currency}
          transactionId={transactionValues.TransactionID}
          parentTransactionId={transactionValues.ParentTransactionID}
          status={transactionValues.TransactionStatus}
          type={'Reversal'}
          transactionDate={transactionValues.TransactionDateTime}
          lastModified={transactionValues.LastModified}
          failureReason={transactionValues.TransactionFailureReason}
        />

        <Card className={cn('card')}>
          <Title level="4" className={cn('title')}>
            {t('transactionDetailsTitle', { defaultValue: 'Transaction Details' })}
          </Title>
          <DetailsRows className={cn('rows')} details={transactionDetails} />
        </Card>
      </Box>

      <>
        {transactionValuesParent && (
          <>
            <Box mt="lg" mb="md">
              <Title level="3">{t('parentTransactionOverview', { defaultValue: 'Parent Transaction Overview' })}</Title>
            </Box>

            {transactionValuesParent.TransactionType === TransactionType.EFT_FUNDING ? (
              <EftFundingTransactionContainer
                TransactionID={transactionValuesParent.TransactionID}
                Notes={transactionValuesParent.Notes}
                isParent={true}
              />
            ) : transactionValuesParent.TransactionType === TransactionType.EFT_WITHDRAWAL ? (
              <EftWithdrawTransactionContainer
                TransactionID={transactionValuesParent.TransactionID}
                Notes={transactionValuesParent.Notes}
                isParent={true}
              />
            ) : transactionValuesParent.TransactionType === TransactionType.ACH_FUNDING ? (
              <AchFundingTransactionContainer
                TransactionID={transactionValuesParent.TransactionID}
                Notes={transactionValuesParent.Notes}
                isParent={true}
              />
            ) : transactionValuesParent.TransactionType === TransactionType.ACH_WITHDRAWAL ? (
              <AchWithdrawalTransactionContainer
                TransactionID={transactionValuesParent.TransactionID}
                Notes={transactionValuesParent.Notes}
                isParent={true}
              />
            ) : transactionValuesParent.TransactionType === TransactionType.INTERACT_MONEY_REQUEST ? (
              <InteracMoneyRequestTransactionContainer
                TransactionID={transactionValuesParent.TransactionID}
                Notes={transactionValuesParent.Notes}
                isParent={true}
              />
            ) : transactionValuesParent.TransactionType === TransactionType.INTERACT_BULK_PAYOUT ? (
              <InteracBulkPayoutTransactionContainer
                TransactionID={transactionValuesParent.TransactionID}
                Notes={transactionValuesParent.Notes}
                isParent={true}
              />
            ) : transactionValuesParent.TransactionType === TransactionType.VOPAYINSTANT_WITHDRAWAL ? (
              <VopayInstantWithdrawalContainer
                TransactionID={transactionValuesParent.TransactionID}
                Notes={transactionValuesParent.Notes}
                isParent={true}
              />
            ) : transactionValuesParent.TransactionType === TransactionType.REFUND ? (
              <RefundTransactionContainer TransactionID={transactionValuesParent.TransactionID} isParent={true} />
            ) : transactionValuesParent.TransactionType === TransactionType.CURRENCY_CONVERSION ? (
              <CurrencyConversionTransactionContainer TransactionID={transactionValuesParent.TransactionID} />
            ) : transactionValuesParent.TransactionType === TransactionType.INBOUND_E_TRANSFER ? (
              <InboundETransferContainer TransactionID={transactionValuesParent.TransactionID} isParent={true} />
            ) : transactionValuesParent.TransactionType === TransactionType.VISA_DIRECT_PUSH ? (
              <VisaDirectPushTransactionContainer
                TransactionID={transactionValuesParent.TransactionID}
                isParent={true}
              />
            ) : transactionValuesParent.TransactionType === TransactionType.CREDIT_CARD ? (
              <CreditCardTransactionContainer TransactionID={transactionValuesParent.TransactionID} isParent={true} />
            ) : (
              <OtherTransactionContainer />
            )}
          </>
        )}
      </>
    </>
  );
};

export default ReversalTransactionInfo;
